import React from 'react';
import { Modal } from '../ui/Modal';

/**
 * La visionneuse d'une facture enregistrée, ouverte depuis l'historique (maquette 8c, type
 * « visionneuse de document » : 960 px). Elle reconstruit le document à partir de ce qui a été
 * enregistré — elle ne recalcule rien.
 *
 * Migrée du `fixed inset-0` maison de FacturesModule.tsx vers le socle de modale : voile
 * translucide, corps qui scrolle sous un en-tête et un pied fixes, Échap, focus piégé.
 */

const fmt = (v: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);

export const InvoicePreviewModal = ({ invoice, onClose }: { invoice: any; onClose: () => void }) => {
    if (!invoice) return null;

    const payload = typeof invoice.payload_json === 'string'
        ? (() => { try { return JSON.parse(invoice.payload_json || '{}'); } catch { return {}; } })()
        : (invoice.payload_json || {});
    const lines: any[] = Array.isArray(payload.lines) ? payload.lines : [];
    const lineTotal = (line: any) => {
        const byRate = Number(Object.values(line.ttcByRate || {}).reduce((a: any, b: any) => Number(a) + Number(b), 0));
        return byRate > 0 ? byRate : Number(line.unit_price_ttc || 0) * Number(line.quantity || 1);
    };
    // Total HT de la ligne : somme des HT taux par taux (une même ligne peut
    // porter plusieurs taux, ex. 10 % et 20 % en restauration). À défaut de
    // ventilation, on retombe sur le taux porté par la ligne elle-même.
    const lineTotalHt = (line: any) => {
        const entries = Object.entries(line.ttcByRate || {});
        if (entries.length > 0) {
            return entries.reduce((sum: number, [rate, ttc]: any) => sum + Number(ttc) / (1 + Number(rate) / 100), 0);
        }
        return lineTotal(line) / (1 + Number(line.tax_rate || 0) / 100);
    };
    const alreadyPaid = Number(invoice.already_paid || 0);
    const totalTtc = Number(invoice.total_ttc || 0);
    const isSettled = totalTtc > 0 && alreadyPaid >= totalTtc;
    const paymentBreakdown: { method: string; amount: number }[] = Array.isArray(payload.paymentBreakdown) ? payload.paymentBreakdown : [];

    return (
        <Modal
            open
            onClose={onClose}
            size="2xl"
            title={`Facture ${invoice.invoice_number || ''}`}
            subtitle={invoice.customer_name || undefined}
            footer={(
                <button
                    type="button"
                    onClick={onClose}
                    className="h-9 px-4 inline-flex items-center rounded-full border border-border bg-surface text-[13px] font-semibold text-content hover:bg-surface-soft whitespace-nowrap shrink-0 transition-colors"
                >
                    Fermer
                </button>
            )}
        >
            <div className="bg-surface border border-slate-200 dark:border-border rounded-xl p-8 max-w-[210mm] mx-auto text-slate-800 dark:text-slate-200">
                <div className="flex justify-between mb-8">
                    <div>
                        <div className="text-xl font-black text-[#2f9e9e] mb-2">{invoice.billing_snapshot?.company_name || 'ÉTABLISSEMENT'}</div>
                        <div className="text-[11px] leading-relaxed opacity-75">
                            {invoice.billing_snapshot?.address}<br/>
                            {invoice.billing_snapshot?.postal_code} {invoice.billing_snapshot?.city}<br/>
                            {invoice.billing_snapshot?.phone && `Tél: ${invoice.billing_snapshot?.phone}`}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-2xl font-black uppercase mb-1">FACTURE</div>
                        <div className="text-xs font-bold mb-1">{invoice.invoice_number}</div>
                        <div className="text-[10px] opacity-60">Date: {new Date(invoice.due_date || invoice.created_at).toLocaleDateString('fr-FR')}</div>
                    </div>
                </div>

                <div className="mb-8">
                    <div className="text-[10px] uppercase font-bold text-[#2f9e9e] mb-2">Facturé à :</div>
                    <div className="font-bold text-sm">{invoice.customer_name}</div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full mb-8 min-w-[500px]">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-border text-[10px] uppercase font-bold text-slate-400">
                                <th className="py-2 text-left">Description</th>
                                <th className="py-2 text-center w-20">Qté</th>
                                <th className="py-2 text-right w-28">P.U. HT</th>
                                <th className="py-2 text-right w-28">P.U. TTC</th>
                                <th className="py-2 text-right w-32">Total HT</th>
                                <th className="py-2 text-right w-32">Total TTC</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                            {lines.map((line: any, i: number) => (
                                <tr key={i} className="text-xs">
                                    <td className="py-3 font-medium min-w-[200px]">{line.label}</td>
                                    <td className="py-3 text-center">{line.quantity}</td>
                                    <td className="py-3 text-right text-content-muted">{Number(line.quantity) > 0 ? fmt(lineTotalHt(line) / Number(line.quantity)) : '-'}</td>
                                    <td className="py-3 text-right text-content-muted">{Number(line.quantity) > 0 ? fmt(lineTotal(line) / Number(line.quantity)) : '-'}</td>
                                    <td className="py-3 text-right text-content-muted">{fmt(lineTotalHt(line))}</td>
                                    <td className="py-3 text-right font-bold">{fmt(lineTotal(line))}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-end">
                    <div className="w-64 space-y-2">
                        <div className="flex justify-between text-xs">
                            <span className="opacity-60 uppercase font-bold">Total HT</span>
                            <span className="font-bold">{fmt(invoice.total_ht)}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="opacity-60 uppercase font-bold">TVA</span>
                            <span className="font-bold">{fmt(invoice.total_tva)}</span>
                        </div>
                        <div className="flex justify-between text-sm border-t border-slate-200 dark:border-border pt-2 text-[#2f9e9e]">
                            <span className="uppercase font-black">Total TTC</span>
                            <span className="font-black text-lg">{fmt(invoice.total_ttc)}</span>
                        </div>

                        {!isSettled && alreadyPaid > 0 && (
                            <div className="flex justify-between text-xs pt-2 border-t border-slate-200 dark:border-border">
                                <span className="opacity-60 uppercase font-bold">Déjà réglé{payload.paymentMethod ? ` (${payload.paymentMethod})` : ''}</span>
                                <span className="font-bold">- {fmt(alreadyPaid)}</span>
                            </div>
                        )}

                        {alreadyPaid > 0 && (
                            <div className="flex justify-between text-sm pt-2 border-t border-slate-200 dark:border-border" style={{ color: isSettled ? '#2f9e9e' : '#dc2626' }}>
                                <span className="uppercase font-black">{isSettled ? 'Total réglé' : 'Reste à payer'}</span>
                                <span className="font-black text-lg">{fmt(isSettled ? totalTtc : Math.max(0, totalTtc - alreadyPaid))}</span>
                            </div>
                        )}

                        {isSettled && (
                            paymentBreakdown.length > 1
                                ? paymentBreakdown.map((entry, i) => (
                                    <div key={i} className="flex justify-between text-xs">
                                        <span className="opacity-60">{entry.method}</span>
                                        <span>{fmt(entry.amount)}</span>
                                    </div>
                                ))
                                : (paymentBreakdown[0]?.method || payload.paymentMethod) ? (
                                    <div className="flex justify-between text-xs">
                                        <span className="opacity-60">Moyen de paiement</span>
                                        <span>{paymentBreakdown[0]?.method || payload.paymentMethod}</span>
                                    </div>
                                ) : null
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};
